// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import { onboardingEn, moreEn } from "./onboarding.en";
import { viewsEn } from "./views.en";
// Bootstrapped from docs/product/main.html's English copy (nav labels, "Log
// in"/"Sign up", app navigation, "Ask Amy") where it already covers the
// shell; new keys (embedded auth per ADR-014, soft-launch banner per
// ADR-015, shared state primitives) are written fresh in the same voice.
export const en = {
    common: {
        appName: "BiteBalance",
        loading: "Loading…",
        retry: "Retry",
        close: "Close",
        dismiss: "Dismiss",
        cancel: "Cancel",
        done: "Done",
        comingSoon: "Coming soon",
        errorGeneric: "Something went wrong.",
        back: "Back",
    },
    nav: {
        tab: { today: "Today", plan: "Plan", shop: "Shop", kitchen: "Kitchen", more: "More" },
        today: "Today",
        plan: "Plan",
        pantry: "Kitchen",
        shop: "Shop",
        insights: "Insights",
        more: "Settings",
        amy: "Amy",
        guides: "Guides",
        signOut: "Sign out",
        openMenu: "Open menu",
        skipToContent: "Skip to content",
        primaryNavigation: "Primary",
    },
    language: {
        label: "Language",
        de: "DE",
        en: "EN",
        switchHint: "Switches to German",
    },
    banner: {
        softLaunch: "BiteBalance is new. Features and content may still change — we'd love your feedback.",
        dismiss: "Dismiss this notice",
    },
    shell: {
        deploy: {
            newVersion: "A new version of BiteBalance is available.",
            reload: "Reload",
            dismiss: "Not now",
        },
    },
    landing: {
        tagline: "Eat for your goals. Spend less. Waste less.",
        logIn: "Log in",
        signUp: "Sign up",
        freeAccessProof: "Free access for 14 days · not a subscription",
    },
    auth: {
        logInTitle: "Log in",
        signUpTitle: "Create your account",
        emailLabel: "Email",
        passwordLabel: "Password",
        passwordHint: "Use 8 or more characters.",
        confirmPasswordLabel: "Confirm password",
        // Composed agreement line (reputable-site pattern): [prefix] Terms-link
        // [connector] Privacy-link [suffix]. Split into pieces so the links are
        // real anchors and both EN/DE word order work. The recorded consent
        // policy version (ADR-012) is a SERVER concern — never shown in the UI.
        agreeSignUpPrefix: "I agree to the",
        agreeSocialPrefix: "By continuing, you agree to our",
        termsLink: "Terms of Service",
        agreeConnector: "and",
        privacyLink: "Privacy Policy",
        agreeSuffix: ".",
        showPassword: "Show password",
        hidePassword: "Hide password",
        aiTransparency: "BiteBalance uses an AI assistant, Amy, to help plan your meals. Amy always proposes changes for you to review — nothing is applied without your approval.",
        createAccount: "Create account",
        freeClaimPerk: "🎁 14 days of free access · not a subscription, no payment details",
        alreadyHaveAccount: "Already have an account?",
        needAccount: "Need an account?",
        verifyTitle: "Check your email",
        verifyIntro: "We sent a verification code to {{email}}. Enter it below to confirm your account.",
        verifyCodeLabel: "Verification code",
        verifyButton: "Confirm account",
        resendCode: "Resend code",
        resendCodeSent: "We sent a new code.",
        signInButton: "Log in",
        forgotPasswordLink: "Forgot your password?",
        forgotTitle: "Reset your password",
        forgotIntro: "Enter your email and we will send you a reset code.",
        sendResetCode: "Send reset code",
        resetTitle: "Set a new password",
        resetIntro: "Enter the code we sent to {{email}} and choose a new password.",
        resetCodeLabel: "Reset code",
        newPasswordLabel: "New password",
        resetButton: "Set new password",
        resetSuccess: "Password updated. You can log in now.",
        backToSignIn: "Back to log in",
        signOutConfirm: "You have been signed out.",
        socialNeedsBuild: "Google sign-in arrives with the installed app — use email for now.",
        continueWithGoogle: "Continue with Google",
        continueWithApple: "Continue with Apple",
        orSeparator: "or",
        socialCompleting: "Completing sign-in…",
        errors: {
            UsernameExistsException: "An account with this email already exists.",
            InvalidPasswordException: "That password does not meet the account's security requirements.",
            InvalidParameterException: "Please check the information you entered.",
            CodeMismatchException: "That code is not correct. Please try again.",
            ExpiredCodeException: "That code has expired. Request a new one.",
            UserNotConfirmedException: "This account is not verified yet. Check your email for a code.",
            UserNotFoundException: "We could not find an account with that email.",
            NotAuthorizedException: "That email or password is not correct.",
            LimitExceededException: "Too many attempts. Please wait a moment and try again.",
            TooManyRequestsException: "Too many attempts. Please wait a moment and try again.",
            TooManyFailedAttemptsException: "Too many attempts. Please wait a moment and try again.",
            ChallengeRequired: "This account needs an additional verification step we don't support yet.",
            MissingRefreshToken: "Your session could not be started. Please try logging in again.",
            OAuthCancelled: "Sign-in was cancelled.",
            OAuthStateMismatch: "We could not verify that sign-in attempt. Please try again.",
            OAuthTokenError: "Sign-in with this provider did not complete. Please try again.",
            generic: "Something went wrong. Please try again.",
            consentRequired: "Please accept the privacy policy and terms to continue.",
            passwordMismatch: "Passwords do not match.",
        },
    },
    state: {
        loading: "Loading…",
        retry: "Try again",
        estimated: "Estimated",
        staleData: "This data may be out of date",
    },
    today: {
        title: "Today",
        emptyTitle: "Your day is not planned yet",
        emptyBody: "Once onboarding and your first plan are ready, today's meals will show up here.",
        signedInAs: "Signed in as {{email}}",
        onboardingNotStarted: "Onboarding has not started yet.",
        onboardingInProgress: "Onboarding is in progress.",
        onboardingCompleted: "Onboarding is complete.",
    },
    plan: {
        title: "Plan",
        emptyTitle: "No week planned yet",
        emptyBody: "Your weekly plan, batch cooking and locked meals will appear here once generated.",
    },
    shop: {
        title: "Shop",
        emptyTitle: "Nothing to shop for yet",
        emptyBody: "Your kitchen and shopping list will appear here once you have a plan.",
    },
    insights: {
        title: "Insights",
        moreLinkSub: "Savings, adherence and your weight trend",
        emptyTitle: "No insights yet",
        emptyBody: "Adherence, savings and time-saved trends will appear here once you have history.",
        learned: {
            title: "What BiteBalance learned",
            caveat: "Only what we can prove from what you logged.",
            mealsLogged: "planned meals logged.",
            streak: "days in a row with a meal logged.",
            streakOne: "day with a meal logged.",
            mealsUnlogged: "{{count}} without an outcome yet.",
            consistency: "of your plan followed so far.",
            realized: "realized savings from confirmed outcomes.",
            projected: "projected savings from this week\u2019s plan.",
            notShownTitle: "Not shown",
            notShownBody: "Energy, mood, sleep, body composition, a \u201chealth score\u201d. BiteBalance does not measure these, so it does not report them.",
        },
        progress: {
            chartTitle: "Your weigh-ins",
            chartSinceStart: "since {{date}}",
            chartEntriesLegend: "Weigh-ins",
            chartTrendLegend: "7-day trend",
            sectionTitle: "Your progress",
            weightLogTitle: "Log your weight",
            weightInputLabel: "Weight (kg)",
            weightDateLabel: "Date",
            logButton: "Log weight",
            loggedConfirmation: "Logged",
            weightErrorInvalid: "Enter a valid weight",
            trendTitle: "7-day average trend",
            scaleGuideLink: "Scale barely moving? Read why the daily number lies",
            trendEmpty: "Log a second weigh-in to see a trend — a single weigh-in is never enough on its own.",
            trendDirectionDown: "Trending down over the period shown",
            trendDirectionFlat: "Roughly flat over the period shown",
            trendDirectionUp: "Trending up over the period shown",
            trendDirectionPending: "A direction will show once you have at least two weeks of data.",
            consistencyTitle: "Plan consistency",
            consistencyBody: "You followed {{percent}}% of your plan so far.",
            unavailableBody: "Progress data is unavailable right now — your logged entries are safe, try again shortly.",
            consistencyEmpty: "Nothing planned yet — consistency will appear once you have a plan.",
            calibratingTitle: "First week is calibration",
            calibratingBody: "Your first week is calibration — keep logging meals and weigh-ins. What you see below is simply what happened; no verdicts yet.",
            observingTitle: "Building your trend",
            observingBody: "Keep logging — the picture gets clearer with more data. Still just observation, no verdicts.",
            reviewableTitle: "You\u2019ve been at this a few weeks",
            reviewableBody: "You've got enough history for a real look together, whenever you'd like.",
            reviewCta: "Review my progress with Amy",
            reviewPrompt: "Can you review my progress with me? Look at my consistency and weight trend and help me think through what's working and what might need adjusting.",
        },
    },
    more: {
        title: "Settings",
        emptyTitle: "Account settings are on their way",
        emptyBody: "Profile, goals, preferences, memory, billing and privacy controls will live here.",
    },
    launch: {
        // A4 fix: the launch gate no longer routes to tabs on a /v1/me failure —
        // an un-onboarded user must never land in an empty app because of a
        // transient network error.
        meErrorTitle: "Couldn't load your account",
        meErrorBody: "We couldn't reach BiteBalance to check your account status. Check your connection and try again.",
    },
    biometrics: {
        offerTitle: "Unlock with {{method}}?",
        offerBody: "Protect your nutrition data on this device: require {{method}} (or your device code) each time the app opens. You can change this anytime under More.",
        enable: "Use {{method}}",
        notNow: "Not now",
        unlockTitle: "BiteBalance is locked",
        unlockPrompt: "Unlock BiteBalance",
        unlockRetry: "Try again",
        usePasscode: "Use device passcode",
        faceIdBlockedHint: "{{method}} not appearing? Check in the iOS Settings app that {{method}} is set up and allowed for BiteBalance. Your device passcode works meanwhile.",
        moreToggle: "App lock ({{method}})",
        moreToggleSub: "Require {{method}} or device code when opening the app",
    },
    privacy: {
        title: "Privacy & data",
        moreLink: "Privacy & data",
        moreLinkSub: "Download your data or delete your account",
        exportTitle: "Download your data",
        exportBody: "Get a complete copy of everything BiteBalance stores about you — profile, plans, kitchen, feedback, memory and chat history — as one JSON file.",
        exportButton: "Create export",
        exportWorking: "Preparing your export…",
        exportDownload: "Download export",
        exportExpiry: "The download link is valid for 15 minutes. You can create a new export anytime.",
        exportFailed: "The export couldn't be created. Please try again.",
        deleteTitle: "Delete your account",
        deleteBody: "This permanently deletes your account and all personal data — profile, plans, kitchen, memories, chat history and login — from all our systems. This cannot be undone. Billing audit records required by law are kept without a link to you.",
        reauthLabel: "Confirm your password to continue",
        reauthButton: "Confirm",
        wrongPassword: "That password didn't match. Please try again.",
        reauthExpired: "Your confirmation expired — please re-enter your password.",
        reconfirmLabel: "For your security, confirm it's you with {{provider}} to continue",
        reconfirmButton: "Continue with {{provider}}",
        reconfirmWorking: "Confirming…",
        reconfirmUsePassword: "I set a password for this account — use it instead",
        reconfirmOtherAccount: "That isn't the account you're signed in with. Please confirm with the same account.",
        reconfirmFailed: "We couldn't confirm your sign-in. Please try again.",
        reconfirmExpired: "Your confirmation expired — please confirm it's you again.",
        deleteConfirmPhrase: "DELETE",
        deleteConfirmLabel: "Type {{phrase}} to confirm permanent deletion",
        deleteButton: "Delete my account permanently",
        deleteWorking: "Deleting…",
        deleteFailed: "Deletion didn't complete. Your account is still active. Please try again.",
        // The request is durably ACCEPTED here, not finished: cleanup runs in the
        // background because AgentCore cannot be purged inside one request. Saying
        // "deleted" at this moment would be a claim we cannot yet make — only a
        // terminal `completed` job means deleted (P1, 2026-09-11).
        deletedTitle: "Account deletion started",
        deletedBody: "You've been signed out and your data will now be permanently deleted. Thank you for trying BiteBalance.",
        // ADR-085 D7 (Apple subscription is not cancelled by account deletion).
        appleSubscriptionWarning: "Deleting your account does not cancel your Apple subscription. Cancel it in your Apple Account settings, otherwise Apple keeps charging you.",
        appleSubscriptionManageLink: "Open Apple subscription settings",
        consentWithdrawLead: "Or withdraw by email:",
        consentWithdrawLink: "Email us at privacy@bitebalance.de.",
        healthConsentTitle: "Consent to health information",
        healthConsentActive: "You have consented to BiteBalance processing your health information for your plan and for Amy.",
        healthConsentWithdraw: "Withdraw consent",
        healthConsentWithdrawConfirmBody: "If you withdraw, we delete your health information (conditions, medications, pregnancy or breastfeeding, allergies and intolerances, weight and height) and stop building plans and answering Amy for you. Existing plans, shopping lists and your chat history stay stored until you delete your account. You can consent again at any time and re-enter your details.",
        healthConsentWithdrawConfirm: "Yes, withdraw and delete my information",
        healthConsentKeep: "Cancel",
        healthConsentWithdrawing: "Withdrawing…",
        healthConsentWithdrawError: "Withdrawing didn't work. Check your connection and try again.",
        healthConsentWithdrawn: "You have withdrawn your consent. We deleted your health information and process no new information.",
        healthConsentNone: "You have not consented at the moment.",
        analyticsLabel: "Product analytics (PostHog)",
        analyticsHelp: "Helps us see which steps are used. Only with your consent.",
        adsLabel: "Marketing (Meta Pixel)",
        adsHelp: "Measures whether our ads on Meta (Facebook, Instagram) lead to sign-ups. Only with your consent.",
        adsLabelApp: "Advertising identifiers for Meta",
        adsHelpApp: "Lets Meta match identifiers from your device with our ads. Anonymous ad measurement runs without it. When you switch it on, iOS asks once for tracking permission; without it the switch stays off.",
        adsTrackingDeniedApp: "iOS currently does not allow BiteBalance to track. You can change this under Settings → Privacy & Security → Tracking.",
        openIosSettings: "Open iOS Settings",
    },
    access: {
        title: "Your access",
        subtitle: "Pick how you want to use BiteBalance. During pre-launch your first month is free — no card needed.",
        pilotNote: "Pilot access — no charge. You're one of our invited testers.",
        activeTitle: "Your plan is active",
        freePlanTitle: "Free plan (beta)",
        freePlanBody: "Full access to plans, Amy, kitchen, shopping lists and insights during the beta. No payment details needed.",
        activateFree: "Activate free plan",
        prelaunchTitle: "Pre-launch free month",
        prelaunchBody: "Full access to plans, Amy, kitchen, shopping lists and insights for one month. No card, nothing renews — it simply ends after 30 days.",
        activatePrelaunch: "Claim your free month",
        freeClaimTitle: "2 weeks free",
        freeClaimBody: "Full access to plans, Amy, kitchen, shopping lists and insights for 2 weeks. No card, nothing renews — it simply ends after 14 days.",
        activateFreeClaim: "Claim your 2 weeks free",
        // ADR-083: automatic free-access activation, right after onboarding —
        // shown both while the (card-free) Whop checkout is being set up and
        // while returning from it, before entitlement is confirmed.
        freeClaimActivating: "Setting up your free access…",
        // Exact founder-specified copy (2026-09-25) — verbatim, do not rephrase.
        freeClaimReady: "Your 14-day free access is ready. No card required.",
        paidIntro: "Or pick your plan now",
        trialBadge: "14 days free",
        trialNote: "then {{price}} · cancel anytime before the trial ends",
        choosePlan: "Start free trial",
        subscribeNow: "Subscribe now",
        returnConfirming: "Welcome back! We're confirming your plan with the payment provider — this page updates automatically.",
        manageTitle: "Cancellation",
        manageBody: "Cancel anytime — your access always continues to the end of the period you've already paid for (or your free month). Nothing further is charged after you cancel.",
        freePrice: "Free",
        badgeEnding: "ending",
        perMonth: "/month",
        perQuarter: " / 3 months",
        perYear: "/year",
        subPrelaunchLine: "🎁 Your free pre-launch month runs until {{date}}. Nothing renews and no card is on file — pick a plan below anytime to continue after.",
        subPrelaunchLineNoDate: "🎁 Your free pre-launch month is active. Nothing renews and no card is on file — pick a plan below anytime to continue after.",
        subPrelaunchLineUrgent: "⏰ Your free pre-launch month ends {{date}} — after that you'll lose access unless you pick a plan below.",
        upgradeFromPrelaunchIntro: "Continue after your free month",
        subFreeClaimLine: "🎁 Your free 2 weeks run until {{date}}. Nothing renews and no card is on file — pick a plan below anytime to continue after.",
        subFreeClaimLineNoDate: "🎁 Your free 2 weeks are active. Nothing renews and no card is on file — pick a plan below anytime to continue after.",
        subFreeClaimLineUrgent: "⏰ Your free 2 weeks end {{date}} — after that you'll lose access unless you pick a plan below.",
        upgradeFromFreeClaimIntro: "Continue after your free 2 weeks",
        switchPlanHint: "Want a different plan? Cancel this one first — you keep full access until the end of the period you've paid for — then pick any plan here.",
        subTrialLine: "🎁 Free trial — ends {{date}}. Your subscription starts right after and you'll be charged {{price}}. Cancel anytime before then and you pay nothing.",
        subRenewLine: "Your subscription will auto-renew on {{date}} at {{price}}.",
        subCancelledLine: "Cancelled — your access continues until {{date}}. Nothing further will be charged.",
        paymentTitle: "Payment",
        paymentBody: "Your payment method is stored securely with Whop, our payment provider — we never see your card details. If a charge ever fails, Whop emails you a secure link to update your card. For anything else, contact us at",
        invoicesTitle: "Invoices & receipts",
        invoicesBody: "Whop emails a receipt to your checkout email for every charge — nothing to look up, it lands in your inbox.",
        cancelCta: "Cancel subscription",
        cancelConfirmBody: "Cancel your plan? You'll keep full access until the end of the current period, and nothing further will be charged. You can start a new plan anytime.",
        cancelConfirmCta: "Yes, cancel my plan",
        cancelKeep: "Keep my plan",
        cancelDone: "Cancelled. Your access continues until {{date}} — nothing further will be charged.",
        cancelDoneNoDate: "Cancelled. Your access continues until the end of the current period — nothing further will be charged.",
        cancelError: "We couldn't cancel just now. Please try again in a moment — or contact us and we'll take care of it.",
        waitingForConfirmation: "Waiting for confirmation…",
        checkoutHint: "Complete the claim in the checkout tab that just opened. This page updates automatically once your access is confirmed.",
        pollTimedOut: "We haven't received the confirmation yet. If you completed the checkout, give it a moment and reopen this page — access is only granted by our server, never by the browser.",
        noOffer: "No plan is available to activate right now. You can continue using the app.",
        comingSoonTitle: "Coming later",
        comingSoon: "Coming soon",
        continue: "Continue to Today",
        loadError: "We couldn't load your access status.",
        checkoutRefused: {
            appleActive: "You already have a BiteBalance subscription through Apple. Manage it in your Apple Account settings.",
            freeUsed: "Your free access has already been used on this account.",
            retired: "Subscriptions are now available in the BiteBalance iPhone app.",
        },
        detailsToggle: "Plan & billing details",
        reducedActiveEyebrow: "🎁 Your access is active",
        reducedFreeUntil: "Free until {{date}}.",
        reducedFreeUntilNoDate: "Your free access is active.",
        reducedNoCharge: "No card on file. No charge today.",
        moreLink: "Plan & billing",
        moreLinkSub: "See your current access and activate a plan",
        // ADR-085 IAP-3: Apple In-App Purchase paywall (mobile only).
        apple: {
            rowUnavailable: "Not available in the App Store right now",
            planUnavailableA11y: "{{plan}}. Not available in the App Store right now.",
            plansPartialNote: "Some plans aren't available in the App Store right now. They'll appear here as soon as they're ready.",
            plansNoneNote: "The App Store didn't return any plans just now. Please try again in a moment.",
            renewsLine: "Renews automatically at {{price}} until you cancel.",
            planA11y: "{{plan}}, {{price}}, renews automatically until you cancel. Subscribe",
            disclosure: "Payment will be charged to your Apple Account when you confirm the purchase. Your subscription renews automatically unless you cancel at least 24 hours before the end of the current period. Your account will be charged for renewal within 24 hours prior to the end of the current period. You can manage or cancel your subscription anytime in your Apple Account settings.",
            termsLink: "Terms of Service",
            privacyLink: "Privacy Policy",
            restoreCta: "Restore purchases",
            restoring: "Checking your purchases…",
            restoreNothing: "We didn't find an active subscription for this Apple Account.",
            restoreDone: "Your subscription is active again on this device.",
            purchasing: "Waiting for Apple…",
            verifying: "Confirming your purchase…",
            applied: "Your plan is active. Thank you!",
            cancelledNote: "No problem — nothing was charged.",
            pendingApproval: "Your purchase is waiting for approval. We'll activate your plan as soon as it's confirmed — you don't need to do anything.",
            interruptedNote: "Apple needs one more step. Once you've completed it, your plan activates on its own.",
            alreadyOwnedNote: "This Apple Account already has a BiteBalance subscription. Tap Restore purchases to use it here.",
            noResultNote: "Nothing was confirmed just now. If you did complete a purchase, your plan activates on its own once Apple confirms it.",
            genericNote: "Something went wrong with the App Store. Please try again later.",
            networkNote: "We couldn't reach the App Store. Check your connection and try again.",
            storeUnavailable: "In-app purchases aren't available on this device right now. You can try again later.",
            unsupportedDevice: "Subscriptions are available in the BiteBalance iPhone app.",
            verifyFailedRetry: "Your purchase is safe, but we couldn't confirm it just now. Please try again.",
            retryCta: "Try again",
            verifyFailedContact: "We couldn't confirm this purchase with Apple. Please contact us at {{email}} and we'll sort it out.",
            notGrantedSandbox: "We received your purchase, but it can't be activated for this account at the moment. If you were charged, contact us at {{email}}.",
            notActive: "That subscription has ended. Pick a plan to continue.",
            otherAccount: "This Apple Account's subscription belongs to a different BiteBalance account. Sign in with that account to use it, or subscribe here with a different Apple Account.",
            featureDisabled: "Subscribing in the app isn't available right now. Please try again later.",
            freeUsedLine: "Your free access has already been used on this account.",
            blockedWhopTitle: "You already have a membership",
            blockedWhopBody: "Your current membership runs until {{date}}. You can choose a plan here once it has ended.",
            blockedWhopBodyNoDate: "Your current membership is still active. You can choose a plan here once it has ended.",
            paymentProblemGrace: "There's a problem with your payment. You keep access until {{date}} — please update your payment details in your Apple Account.",
            paymentProblemPaused: "Your payment is being retried and access is paused. Update your payment details in your Apple Account, or choose a plan below.",
            manageTitle: "Manage subscription",
            manageBody: "Change your plan or cancel in your Apple Account settings. Cancelling keeps your access until the end of the period you've paid for.",
            manageCta: "Open Apple subscription settings",
            paymentTitle: "Payment & receipts",
            paymentBody: "Apple handles your payment and emails you a receipt for every charge. We never see your card details. Questions? Contact us at",
            renewLine: "Renews on {{date}} at {{price}}.",
            renewLineNoPrice: "Renews on {{date}}.",
            // App Review 2026-10-30 (3.1.2(c)): the free access is SEPARATE from the subscriptions — never a trial, never a plan.
            freeAccessName: "Free access",
            freeAccessLine: "Free access until {{date}}. This is not a subscription: no payment details, nothing renews, nothing is charged.",
            freeAccessLineNoDate: "Your free access is active. This is not a subscription: no payment details, nothing renews, nothing is charged.",
            freeAccessLineUrgent: "Your free access ends on {{date}}. To keep using BiteBalance after that, you can buy a subscription.",
            subscriptionsHeading: "Subscriptions",
            subscriptionsNote: "The subscriptions have no free trial: Apple charges the price when a subscription starts, and it renews automatically until you cancel.",
            freeAccessEyebrow: "Your access is active",
            freeAccessUntil: "Free access until {{date}}.",
            freeAccessUntilNoDate: "Your free access is active.",
            freeAccessSummaryNote: "Not a subscription. No payment details, nothing is charged.",
            freeAccessOfferTitle: "Free access for 14 days",
            freeAccessOfferBody: "Use every feature for 14 days at no cost. This is separate from the subscriptions: no payment details, nothing renews, nothing to cancel.",
            freeAccessOfferCta: "Start free access",
            freeAccessActivating: "Setting up your free access…",
            freeAccessReady: "Your free access is ready: 14 days, not a subscription, no payment details.",
            accessActive: "Your access is active",
        },
        web: {
            subtitle: "Your access to BiteBalance and how to keep it.",
            appStoreOnlyTitle: "Subscribe in the BiteBalance iPhone app",
            appStoreOnlyBody: "Plans and prices are shown in the app, and you pay through your Apple Account. Everything you do here on the web stays in sync with the app.",
            noIphoneNote: "Don't have an iPhone? Your free access works here on the web too; a subscription is currently only available through the iPhone app.",
            freeStartTitle: "Free access for 14 days",
            freeStartBody: "Full access for 14 days. This is not a subscription: no payment details, nothing renews, nothing to cancel. After that, you can buy a subscription in the BiteBalance iPhone app.",
            freeStartCta: "Start free access",
            freeStartFailed: "We couldn't start your free access just now. Please try again in a moment.",
            continueInAppLine: "To keep going after {{date}}, choose a plan in the BiteBalance iPhone app.",
            continueInAppLineNoDate: "To keep going, choose a plan in the BiteBalance iPhone app.",
            appleRenewsOn: "Renews on {{date}}. Price and payment are handled by Apple.",
            appleManagedLine: "This subscription is managed in your Apple Account.",
            paymentProblemPaused: "Your payment is being retried and access is paused. Update your payment details in your Apple Account.",
        },
        planName: {
            free: "Free plan",
            prelaunch: "Pre-launch free month",
            freeClaim: "2-Week Free Plan",
            monthly: "Monthly",
            quarterly: "3 months",
            annual: "Yearly",
        },
        source: {
            pilot: "Pilot access",
            individual: "Individual plan",
            b2b: "Team access",
            none: "No plan",
        },
        state: {
            active: "active",
            trialing: "trial",
            past_due: "payment due",
            canceled: "canceled",
            expired: "expired",
            none: "not active",
        },
    },
    consent: {
        body: "We'd like to use cookies to understand how BiteBalance is found and used, so we can improve it. We only turn this on with your permission.",
        accept: "Accept",
        decline: "Decline",
        webBody: "We would like to use two optional services: PostHog, to understand how BiteBalance is used, and the Meta Pixel, to measure whether our ads on Facebook and Instagram lead to sign-ups. Both only with your consent.",
        analyticsOption: "Product analytics (PostHog)",
        adsOption: "Marketing (Meta Pixel)",
        appBody: "We'd like to use PostHog to understand how BiteBalance is used, only with your consent. For ad measurement we use the Meta SDK without an advertising identifier; whether Meta may additionally use identifiers from your device is your choice here. If you allow advertising identifiers, iOS then asks you once for tracking permission.",
        adsOptionApp: "Advertising identifiers for Meta",
        save: "Save selection",
        acceptAll: "Accept all",
        settingsLink: "Cookie settings",
    },
    healthConsent: {
        eyebrow: "Your consent",
        title: "Can BiteBalance work with your health information?",
        body: "To build your plan we process your health data within the meaning of Art. 9 GDPR: conditions, medications, pregnancy or breastfeeding, allergies and intolerances, and weight and height. Our AI Amy uses Claude by Anthropic via Amazon Bedrock in the EU for this. Your messages and photos to Amy go there too. Anthropic does not get access to your data; Amazon Web Services processes it on our behalf in the EU and may store inputs and outputs for a limited time to detect abuse.",
        checkbox: "I explicitly consent to BiteBalance processing my health information for this (Art. 9(2)(a) GDPR).",
        footnote: "You can withdraw this consent at any time in the app under More → Privacy & data, by emailing privacy@bitebalance.de or by deleting your account. We then delete your health information. Without consent we can't build a plan for you.",
        privacyLink: "Privacy notice",
        continue: "Continue",
        saving: "Saving…",
        saveError: "That didn't work. Check your connection and try again.",
        declineHint: "Don't agree? You can download your data or delete your account.",
        backToConsent: "Back to consent",
    },
    amy: {
        panel: {
            expand: "Expand Amy",
            collapse: "Shrink Amy",
            close: "Close Amy",
            widen: "Widen Amy",
            narrow: "Narrow Amy",
        },
        editMessage: "Edit message",
        editingNotice: "You're editing an earlier message. The new version is added at the end.",
        editCancel: "Cancel edit",
        attachPhoto: "Photo",
        attachFile: "File",
        attachTitle: "Add to your message",
        fileTooLarge: "That file is too large (max ~3 MB).",
        fileUnsupported: "Supported files: PDF, CSV, TXT, MD, DOCX, XLSX.",
        fileChipRemove: "Remove file",
        micDevBuildHint: "Voice input arrives with the installed app — meanwhile, tap the mic key on your keyboard.",
        suggestionsLabel: "Try asking",
        copyMessage: "Copy message",
        copiedMessage: "Copied",
        title: "Amy",
        askAmy: "Ask Amy",
        askAmyHint: "Opens Amy, BiteBalance's AI assistant.",
        openButton: "Open Amy",
        closeButton: "Close Amy",
        inputLabel: "Message Amy",
        inputPlaceholder: "e.g. Make this week cheaper",
        send: "Send",
        thinking: "Amy is thinking\u2026",
        toolRunning: "Checking with your plan data\u2026",
        error: "Amy couldn't answer just now. Please try again.",
        // ADR-058 decision 5 \u2014 shown INSTEAD of a resend once text or a tool
        // already came back: a retry could duplicate what Amy was doing.
        interrupted: "Amy's reply was interrupted. If she was changing something, check Today and Plan before asking again.",
        turn: {
            stillWorking: "Amy is still working on this \u2014 you can leave and come back.",
            answeredWhileAway: "Answered while you were away",
            couldNotFinish: "Amy couldn't finish this one. If she was changing something, check Today and Plan before asking again.",
            notReceived: "Your message didn't reach Amy. Please send it again.",
            answeredTitle: "Amy answered",
            answeredBody: "Your answer is ready in BiteBalance.",
        },
        disclaimer: "Amy proposes \u2014 nothing changes without your explicit approval, and every change passes the same safety checks as the rest of the app.",
        emptyHint: "Ask Amy about your week \u2014 swaps, cheaper options, or what's in a recipe.",
        // 2026-08 greeting redesign (Google Health "Coach"-style opaque welcome
        // card, item 7): personalized hi, why-share nudge, and three tappable
        // example prompts that insert into the input rather than auto-send.
        greeting: {
            hiName: "Hi {{name}}!",
            hiGeneric: "Hi there!",
            subtitle: "The more you share, the smarter your plan gets.",
            promptsLabel: "Try asking",
            promptAteOut: "I ate out at lunch \u2014 can my day still work?",
            promptCheaper: "Make this week cheaper",
            promptRecipe: "What's in tonight's recipe?",
        },
        historyLoading: "Loading your recent conversation\u2026",
        historyUnavailable: "Amy's memory couldn't be reached just now \u2014 you can still chat, but earlier messages may not show up.",
        // ADR-068 Fresh Adaptive Amy (mobile): grounded opening state + composer copy.
        headerSub: "Knows your plan, kitchen and limits",
        sees: {
            title: "What Amy sees right now",
            eatenToday: "Eaten today",
            nextUp: "Next up",
            week: "This week",
            kitchen: "Kitchen",
            allLogged: "All logged",
            meal: "1 meal",
            meals: "{{n}} meals",
            expired: "{{name}} has expired",
            expiresToday: "{{name}} expires today",
            expiresTomorrow: "{{name}} expires tomorrow",
            nothingExpiring: "Nothing expiring",
        },
        composerPlaceholder: "Ask Amy anything about this week",
        composerSend: "Ask",
        sendWhileBusy: "Amy is answering — you can keep typing and send when she's done.",
        // F4 \u2014 voice input (web SpeechRecognition; mobile mic lands with the dev build, D-7)
        voice: {
            start: "Dictate your message",
            stop: "Stop dictation",
            listening: "Listening\u2026 tap the microphone again to stop.",
            permissionDenied: "BiteBalance doesn't have microphone access. You can allow it in your browser's site settings \u2014 or just keep typing.",
            error: "Voice input didn't work just now. Please try again or keep typing.",
        },
        // F5 \u2014 photo attachments (meal, fridge, receipt, or product-label photos)
        photo: {
            attach: "Attach a photo",
            remove: "Remove photo",
            previewAlt: "Photo ready to send",
            bubbleAlt: "Photo you sent",
            placeholder: "[Photo]",
            defaultPrompt: "What can you tell me about this photo?",
            tooLarge: "This photo is too large even after compression. Please choose a smaller one.",
            readError: "This photo couldn't be read. Please try a different one.",
            sourceTitle: "Add a photo",
            takePhoto: "Take photo",
            chooseFromLibrary: "Choose from library",
            cameraPermissionDenied: "BiteBalance doesn't have camera access. You can allow it in your device settings, or pick a photo from your library instead.",
        },
    },
    onboarding: onboardingEn,
    moreProfile: moreEn,
    amyCompanion: {
        morningTitle: "Start the day with Amy",
        morningSubtitle: "A quick word with Amy sets up the whole day.",
        morningChip1: "What should I focus on today?",
        morningChip2: "Can we make today a bit cheaper?",
        dayTitle: "How's your day going?",
        daySubtitle: "Plans change — tell Amy and she'll rebalance the rest.",
        dayChip1: "I ate something different for lunch",
        dayChip2: "What's planned for dinner tonight?",
        eveningTitle: "Evening check-in",
        eveningSubtitle: "Two taps and tomorrow already fits.",
        eveningChip1: "I ate off-plan today — help me rebalance",
        eveningChip2: "How did I do today?",
        snapChip: "Snap what you're eating",
    },
    checkin: {
        toggleLabel: "Daily evening check-in",
        toggleBody: "A friendly reminder from Amy around 8 pm to tell her how the day went. Stored on this device only.",
        permissionDenied: "Notifications are off for BiteBalance in your device settings.",
        notifTitle: "Evening check-in 🌙",
        notifBody: "How did today go? Two taps and tomorrow already fits.",
        mealTitle: "How was {{meal}}?",
        mealBody: "Mark it, or tell Amy what changed — your day stays accurate.",
    },
    prehook: {
        eyebrow: "BUILT FOR YOU",
        eyebrowNamed: "BUILT FOR {{name}}",
        title: "Your week already exists.",
        readyTitle: "Your week is ready.",
        intro: "We built it while you finished — {{days}} days, {{meals}} meals, checked line by line against what you told us.",
        scoreOf: "/100",
        fitSeal: "Fit check",
        saidGoal: "You said: {{goal}}",
        receiptGoal: "Daily target {{kcal}} — inside safe, capped bounds.",
        saidTime: "You said: about {{limit}} min to cook",
        receiptTimeOk: "Your main meals average {{avg}} min hands-on.",
        receiptTimeOver: "They average {{avg}} min right now — Amy can swap the slow ones.",
        saidNoTime: "You skipped a time limit",
        receiptNoTime: "Every recipe shows its minutes anyway.",
        saidBudget: "You said: about {{budget}} a week",
        receiptBudgetOk: "Your week comes to {{cost}} — {{delta}} under.",
        receiptBudgetOver: "Your week comes to {{cost}} — {{delta}} over. Swaps can close it.",
        saidNoBudget: "You skipped a budget",
        receiptNoBudget: "Costs are shown per serving anyway.",
        saidFood: "You said: {{style}}",
        receiptFood: "0 conflicts across {{meals}} meals · {{distinct}} different dishes.",
        receiptFoodPending: "Still adjusting this week to your rules.",
        teaseHeading: "First up from your week",
        teaseSlot: "{{day}} · {{slot}}",
        teaseMore: "…and {{count}} more meals waiting.",
        firstUpSuffix: "first up",
        fitPill: "{{score}}/100 fit",
        countsPill: "{{meals}} meals · {{days}} days",
        disclosureLabel: "See how we matched your answers",
        cta: "Open my week",
        // ADR-083 (2026-09-25): the automatic pilot grant this line referred to
        // was retired 2026-09-12 (ADR-073) — corrected to describe what's
        // actually true now, the automatic 14-day free-access activation.
        ctaNote: "Your 14-day free access is included — not a subscription, no payment details.",
        footnote: "Every line above comes from your actual plan.",
    },
    planSync: {
        updatingTitle: "Updating your plan",
        targetChangedLine: "Your daily calorie target is now {{to}} (was {{from}}).",
        updatingBody: "We're rebuilding your plan for today and the coming week so it matches your settings. This takes a moment.",
        doneTitle: "Plan updated",
        doneBody: "Your plan for today and the coming week now matches your settings.",
        doneBodyWithTarget: "Your plan for today and the coming week now fits your {{to}} daily target.",
        errorTitle: "We couldn't update your plan",
        errorBody: "Your new settings are saved, but the plan rebuild failed. Your current plan is unchanged — try again.",
        retryCta: "Try again",
    },
    views: viewsEn,
};
